<template>
    <vx-modal
        v-model:visible="visible"
        :size="reviewable ? 'xl' : 'md'"
        padding="standard"
        :stacked="decisionModalVisible"
    >
        <template v-slot:header>Run #{{ runId }}</template>
        <template v-slot:subheader>{{ run?.team_name ?? '' }}</template>

        <page-loading v-if="loading"></page-loading>

        <div v-else-if="run !== null" class="flex flex-col gap-6 text-sm text-slate-800">
            <dl class="m-0 grid grid-cols-[auto_1fr] gap-x-6 gap-y-2">
                <dt class="font-bold">Status</dt>
                <dd class="m-0"><kiosk-rnd-run-status :run="run" /></dd>
                <template v-if="run.stop_reason !== null">
                    <dt class="font-bold">Stop reason</dt>
                    <dd class="m-0" data-test="run-stop-reason">
                        {{ stopReasonLabel(run) }}
                        <span v-if="stopDetailLabel(run) !== null" class="text-slate-500"
                            >· {{ stopDetailLabel(run) }}</span
                        >
                    </dd>
                </template>
                <dt class="font-bold">Team</dt>
                <dd class="m-0">
                    <kiosk-team-cell :teamId="run.team_id" :teamName="run.team_name" :teamStatus="run.team_status" />
                </dd>
                <dt class="font-bold">RND Company ID</dt>
                <dd class="m-0 font-mono" data-test="run-company-id">{{ run.company_id }}</dd>
                <dt class="font-bold">Consent date</dt>
                <dd class="m-0" data-test="run-consent-source">
                    <template v-if="run.consent_attribute_key !== null">
                        Attribute <span class="font-mono">{{ run.consent_attribute_key }}</span
                        >, else the Voxie opt-in timestamp
                    </template>
                    <template v-else>Voxie opt-in timestamp</template>
                </dd>
                <dt class="font-bold">Run by</dt>
                <dd class="m-0" data-test="run-by">{{ runByLabel(run) }}</dd>
                <dt class="font-bold">Type</dt>
                <dd class="m-0 flex flex-wrap items-center gap-1" data-test="run-type">
                    {{ runTypeLabel(run) }}
                    <template v-if="run.source_run_id !== null">
                        of
                        <button
                            type="button"
                            data-test="source-run"
                            :class="linkClasses"
                            @click="runId = run.source_run_id"
                        >
                            #{{ run.source_run_id }}
                        </button>
                    </template>
                    <template v-if="run.followed_up_by_run_id !== null">
                        · followed up by
                        <button
                            type="button"
                            data-test="followed-up-by-run"
                            :class="linkClasses"
                            @click="runId = run.followed_up_by_run_id"
                        >
                            #{{ run.followed_up_by_run_id }}
                        </button>
                    </template>
                </dd>
                <dt class="font-bold">Review resets</dt>
                <dd class="m-0" data-test="run-review">{{ run.requires_approval ? 'On' : 'Off' }}</dd>
                <dt class="font-bold">Started</dt>
                <dd class="m-0" data-test="run-started">{{ runDateLabel(run.snapshot_at) }}</dd>
                <dt class="font-bold">Finished</dt>
                <dd class="m-0" data-test="run-finished">{{ runDateLabel(run.finished_at) }}</dd>
            </dl>

            <kiosk-rnd-run-approval v-if="reviewable" :key="run.id" :run="run" />

            <dl v-if="run.counts !== null" class="m-0 grid grid-cols-[1fr_auto] gap-x-6 gap-y-1" data-test="run-counts">
                <dt>Contacts in the snapshot</dt>
                <dd class="m-0 text-right tabular-nums" data-test="count-snapshot">
                    {{ countLabel(run.counts.snapshot) }}
                </dd>
                <dt>Queried</dt>
                <dd class="m-0 text-right tabular-nums" data-test="count-queried">
                    {{ countLabel(run.counts.queried) }}
                </dd>
                <dt>Reassigned</dt>
                <dd class="m-0 text-right tabular-nums" data-test="count-reassigned">
                    {{ countLabel(run.counts.reassigned) }}
                </dd>
                <template v-for="row in reassignedRows(run.counts.reassigned_breakdown)" :key="row.key">
                    <dt class="pl-4 text-slate-500">{{ row.label }}</dt>
                    <dd class="m-0 text-right tabular-nums text-slate-500" :data-test="`count-${row.key}`">
                        {{ countLabel(row.count) }}
                    </dd>
                </template>
                <dt>Not reassigned</dt>
                <dd class="m-0 text-right tabular-nums" data-test="count-no">{{ countLabel(run.counts.no) }}</dd>
                <dt>No data</dt>
                <dd class="m-0 text-right tabular-nums" data-test="count-no-data">
                    {{ countLabel(run.counts.no_data) }}
                </dd>
                <dt>Not checked</dt>
                <dd class="m-0 text-right tabular-nums" data-test="count-not-checked">
                    {{ countLabel(run.counts.not_checked) }}
                </dd>
                <template v-for="row in notCheckedRows(run.counts.not_checked_breakdown)" :key="row.key">
                    <dt class="pl-4 text-slate-500">{{ row.label }}</dt>
                    <dd class="m-0 text-right tabular-nums text-slate-500" :data-test="`count-${row.key}`">
                        {{ countLabel(row.count) }}
                    </dd>
                </template>
                <dt>Billed queries</dt>
                <dd class="m-0 text-right tabular-nums" data-test="count-billed">
                    {{ countLabel(run.counts.billed) }}
                </dd>
            </dl>
            <div v-else class="text-slate-500" data-test="run-no-counts">Nothing has been counted yet.</div>

            <section v-if="run.events.length > 0" class="flex flex-col gap-2" data-test="run-events">
                <h3 class="m-0 text-sm font-bold">Events</h3>
                <ol class="m-0 flex list-none flex-col gap-1 p-0">
                    <li
                        v-for="(event, index) in run.events"
                        :key="index"
                        class="flex flex-wrap gap-x-2"
                        data-test="run-event"
                    >
                        <span class="text-slate-500">{{ shortDateTime(event.created_at) }}</span>
                        <span>{{ runEventLabel(event) }}</span>
                        <span v-if="runEventActor(event) !== null" class="text-slate-500">
                            by {{ runEventActor(event) }}
                        </span>
                    </li>
                </ol>
            </section>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    type="button"
                    class="grow"
                    size="lg"
                    color="muted"
                    data-test="close-run"
                    @click="visible = false"
                >
                    Close
                </vx-button>
                <template v-if="reviewable">
                    <vx-button
                        type="button"
                        class="grow"
                        size="lg"
                        color="danger"
                        outline
                        data-test="deny-run"
                        @click="decide('deny')"
                    >
                        Deny
                    </vx-button>
                    <vx-button
                        type="button"
                        class="grow"
                        size="lg"
                        color="primary"
                        data-test="approve-run"
                        @click="decide('approve')"
                    >
                        Approve
                    </vx-button>
                </template>
            </div>
        </template>
    </vx-modal>

    <kiosk-rnd-run-decision-modal
        v-if="run !== null && action !== null"
        v-model:visible="decisionModalVisible"
        :run="run"
        :action="action"
        @decided="reloadAfterDecision"
        @conflict="reloadAfterDecision"
    />
</template>

<script lang="ts" setup>
import { VxButton, VxModal } from '@voxie/frontend-components';
import { isCancel } from 'axios';
import { computed, ref, watch } from 'vue';
import PageLoading from '~/components/general/PageLoading.vue';
import KioskTeamCell from '~/components/kiosk/KioskTeamCell.vue';
import {
    canReviewRun,
    countLabel,
    notCheckedRows,
    reassignedRows,
    runByLabel,
    runDateLabel,
    runEventActor,
    runEventLabel,
    runTypeLabel,
    stopDetailLabel,
    stopReasonLabel,
} from '~/components/kiosk/kiosk-reassigned-numbers/rndLabels';
import KioskRndRunApproval from '~/components/kiosk/kiosk-reassigned-numbers/runs/KioskRndRunApproval.vue';
import KioskRndRunDecisionModal from '~/components/kiosk/kiosk-reassigned-numbers/runs/KioskRndRunDecisionModal.vue';
import KioskRndRunStatus from '~/components/kiosk/kiosk-reassigned-numbers/runs/KioskRndRunStatus.vue';
import type { RndRunApprovalAction } from '~/components/kiosk/kiosk-reassigned-numbers/runs/kioskRndApprovalHelpers';
import { useToasted } from '~/composables/useToasted';
import { adminRndClient } from '~/services';
import type { RndRunDetails } from '~/services/adminRndClient.types';
import { shortDateTime } from '~/utils/date';
import { catchRequestError } from '~/utils/request';

const runId = defineModel<number | null>('runId', { default: null });
const emit = defineEmits<{ changed: [] }>();

const toasted = useToasted();

const runCancelKey = Symbol('kiosk-rnd-run');
const linkClasses =
    'cursor-pointer border-0 bg-transparent p-0 font-bold text-sky-700 hover:underline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500';

const run = ref<RndRunDetails | null>(null);
const loading = ref(false);
const action = ref<RndRunApprovalAction | null>(null);
const decisionModalVisible = ref(false);

const reviewable = computed(() => run.value !== null && canReviewRun(run.value));

const visible = computed({
    get: () => runId.value !== null,
    set: (open: boolean) => {
        if (!open) {
            runId.value = null;
        }
    },
});

const loadRun = async (id: number) => {
    loading.value = true;
    run.value = null;

    try {
        const { data } = await adminRndClient.getRun(id, { cancelKey: runCancelKey });

        run.value = data;
        loading.value = false;
    } catch (error) {
        if (isCancel(error)) {
            return;
        }

        loading.value = false;
        runId.value = null;
        toasted.error(catchRequestError(error));
    }
};

const decide = (next: RndRunApprovalAction) => {
    action.value = next;
    decisionModalVisible.value = true;
};

const reloadAfterDecision = () => {
    emit('changed');

    if (runId.value !== null) {
        loadRun(runId.value);
    }
};

watch(
    runId,
    (id) => {
        if (id !== null) {
            loadRun(id);
        }
    },
    { immediate: true }
);
</script>
